import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    TemplateRef,
    ViewChild,
} from "@angular/core";
import { AlertController, IonSearchbar } from "@ionic/angular";
import { Select, Store } from "@ngxs/store";
import { Observable, Subject } from "rxjs";
import { ConfigState } from "src/store/config/config.state";
import { SearchProjects } from "src/store/projects/actions/search-project.action";
import { SetSearchBarStatus } from "src/store/projects/actions/set-search-bar.action";
import moment from "moment";
import { AppState } from "src/store/app/app.state";
import { ProjectsState } from "src/store/projects/projects.state";
import { filter, take, takeUntil } from "rxjs/operators";
import { ProjectsService } from "src/app/services/projects.service";
import { ProjectData } from "src/app/services/response-models/projects.response.model";
import { TranslateService } from "@ngx-translate/core";
import { NavigationEnd, Router } from "@angular/router";
import {
    ClearUserIntervals,
    SetCheckInIntervals,
} from "src/store/users/actions/set-checkin-intervals.action";
import { UsersState } from "src/store/users/users.state";
import { UserCheckIn } from "src/app/employee/projects/check-in-out/check-in-out";

@Component({
    selector: "header",
    templateUrl: "./header.component.html",
    styleUrls: ["./header.component.scss"],
    standalone: false,
})
export class HeaderComponent implements OnInit, OnDestroy {
    @Select(ConfigState.getPlatform) platform$: Observable<string>;
    @Select(ProjectsState.getSearchQuery) query$: Observable<string>;
    @Select(ProjectsState.getIsSearchBarOpen) isSeachBarHidden$: Observable<boolean>;
    @Select(AppState.getMenuStatus) hasMenu$: Observable<string>;

    @ViewChild("searchbar") searchbar: IonSearchbar;

    @Input() title: string;
    @Input() hasSearch: boolean = false;
    @Input() entityType: "projects" | "offers" = "projects";

    @Input() optionsTemplate: TemplateRef<any>;

    @Input() backArrow: boolean = false;
    @Input() backUrl: string;

    @Output() onSearch = new EventEmitter();
    @Output() onBackButtonClick = new EventEmitter();

    public isSearchbarHidden = true;

    public checkOutTimer: string;
    public checkinProject: ProjectData;
    public showCheckinBar = true;
    public checkedAt: string = null;

    private checkInRecords = [];
    private interval: number;
    private readonly unsubscribe$ = new Subject<void>();

    constructor(
        private store: Store,
        private projectService: ProjectsService,
        private alertCtrl: AlertController,
        private translateService: TranslateService,
        private userCheckin: UserCheckIn,
        private router: Router
    ) {}

    public ngOnInit() {
        this.router.events
            .pipe(
                filter((x) => x instanceof NavigationEnd),
                takeUntil(this.unsubscribe$)
            )
            .subscribe((data: NavigationEnd) => {
                this.showCheckinBar =
                    data.url.includes("projects") ||
                    data.url.includes("profile") ||
                    data.url.includes("calendar");
            });
        this.setCheckInStatus();
    }

    private setCheckInStatus(): void {
        this.store
            .select(AppState.getUserClockingRecords)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((d) => {
                this.checkInRecords = d;

                if (d.length === 0) {
                    this.clearIntervals();
                    this.checkOutTimer = null;
                    return;
                }

                this.getCheckinProjectName(d[0].project_id);

                this.interval = setInterval(() => {
                    if (!this.checkInRecords.length) {
                        this.clearIntervals();
                        return;
                    }

                    const checkInTime = moment(d[0].checked_in);
                    const now = moment();

                    const diff = now.diff(checkInTime, "seconds");
                    // if diff is negative this means client's clock
                    // is behind or checkin in the future and we will show the bar only in certain pages
                    // with information when the checkin will start
                    if (diff < 0) {
                        this.checkedAt = checkInTime.format(checkInTime.format("HH:mm"));
                        return;
                    }

                    const timeFromStart = moment.duration(diff, "seconds");
                    const hours =
                        timeFromStart.hours() < 10
                            ? `0${timeFromStart.hours()}`
                            : timeFromStart.hours();
                    const minutes =
                        timeFromStart.minutes() < 10
                            ? `0${timeFromStart.minutes()}`
                            : timeFromStart.minutes();
                    const seconds =
                        timeFromStart.seconds() < 10
                            ? `0${timeFromStart.seconds()}`
                            : timeFromStart.seconds();

                    this.checkOutTimer = `${hours}:${minutes}:${seconds}`;
                }, 1000) as any;

                this.store.dispatch(new SetCheckInIntervals([this.interval]));
            });
    }

    public ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    public toggleSearchBar(): void {
        this.isSearchbarHidden = !this.isSearchbarHidden;
        this.store.dispatch(new SetSearchBarStatus(this.isSearchbarHidden));
    }

    public onSearchHandler(event): void {
        this.store.dispatch(new SearchProjects(event.detail.value));
        this.onSearch.emit(event.detail.value);
    }

    public backButtonClickHandler(): void {
        this.onBackButtonClick.emit();
    }

    // on click
    public async checkOut(): Promise<void> {
        const translations = this.translateService.instant("CHECK_OUT_ALERT");
        const buttons = [
            {
                role: "dismiss",
                text: translations.CANCEL,
            },
            {
                text: translations.CONFIRM,
                handler: () => {
                    this.userCheckin.checkOut();
                },
            },
        ];
        const alert = await this.alertCtrl.create({
            header: translations.HEADER,
            message: `${translations.MESSAGE} ${this.checkinProject.name}?`,
            buttons,
        });

        alert.present();
    }

    private getCheckinProjectName(project_id: string) {
        this.store
            .select(ProjectsState.getProjects)
            .pipe(
                filter((projects) => !!projects),
                take(1)
            )
            .subscribe((data) => {
                const project = data.find((p) => p.id === project_id);
                // get the project if it is not in store
                if (!project) {
                    this.projectService
                        .getProjectById(project_id, "", true)
                        .then((project: ProjectData) => {
                            this.checkinProject = project;
                        });
                    return;
                }

                this.checkinProject = project;
            });
    }

    private clearIntervals(): void {
        this.checkedAt = null;
        const intervals = this.store.selectSnapshot(UsersState.getIntervals);

        for (const interval of intervals) {
            clearInterval(interval);
        }

        this.store.dispatch(new ClearUserIntervals());
    }
}
